import { useState } from 'react'
import * as React from 'react'
import useIsomorphicLayoutEffect from './useIsomorphicLayoutEffect'
import { getElementTextDirection, type TextDirection } from 'lib/dom/logicalScroll'

export default function useTextDirection(
  ref: React.RefObject<HTMLElement | null>,
): TextDirection {
  const [textDirection, setTextDirection] = useState<TextDirection>('ltr')

  useIsomorphicLayoutEffect(() => {
    const element = ref.current

    if (!element) return

    const updateTextDirection = () => {
      const nextTextDirection = getElementTextDirection(element)
      setTextDirection(currentTextDirection => currentTextDirection === nextTextDirection ? currentTextDirection : nextTextDirection)
    }

    updateTextDirection()

    const MutationObserverConstructor = typeof MutationObserver !== 'undefined' ?
      MutationObserver :
      typeof window !== 'undefined' ? window.MutationObserver : undefined

    if (typeof document === 'undefined' || !MutationObserverConstructor) return

    const observer = new MutationObserverConstructor(updateTextDirection)
    const observedElements = element === document.documentElement ? [element] : [document.documentElement, element]

    observedElements.forEach((observedElement) => {
      observer.observe(observedElement, {
        attributes: true,
        attributeFilter: ['dir'],
      })
    })

    return () => observer.disconnect()
  }, [ref])

  return textDirection
}
